import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/UI/table';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import srcHeroPattern from './images/hero-pattern.svg';

interface HeroProps {
  nflAvailable?: boolean;
  cfbAvailable?: boolean;
}

export default function Hero({ nflAvailable = false, cfbAvailable = false }: HeroProps) {
  const availableSports = [nflAvailable && 'NFL', cfbAvailable && 'CFB']
    .filter(Boolean)
    .join(' & ');

  const nflResults = [
    { season: 'Last 3', win: 153, loss: 123, push: 5, units: 18.22 },
    { season: 'Last 5', win: 206, loss: 169, push: 6, units: 21.37 },
  ];

  const cfbResults = [
    { season: 'Last 3', win: 240, loss: 179, push: 6, units: 45.2 },
    { season: 'Last 5', win: 311, loss: 218, push: 9, units: 73.25 },
  ];

  function toFixed(num?: number) {
    if (typeof num !== 'number') {
      return num;
    }
    return (num || 0).toFixed(2);
  }

  return (
    <div className="relative isolate overflow-hidden">
      {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
      <div
        className="bg-repeat"
        style={{ backgroundImage: `url("${encodeURI(decodeURIComponent(srcHeroPattern))}")` }}
      >
        <div className="border-zinc-600/80 border-b bg-linear-to-bl from-emerald-400/50 to-70% px-4 lg:px-16">
          <div className="mx-auto max-w-6xl pt-32 pb-12 sm:py-24">
            <div className="mt-6 flex flex-col justify-center gap-x-8 gap-y-20 lg:flex-row lg:px-8 xl:px-16">
              <div className="my-auto text-left">
                {availableSports && (
                  <div className="mx-auto mb-4 flex w-fit items-center gap-2 rounded-full bg-emerald-500/20 px-3 py-1 font-semibold text-emerald-400 text-xs ring-1 ring-emerald-500/40 lg:mx-0">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                    2026 {availableSports} — now open
                  </div>
                )}
                <h1 className="mx-auto max-w-[310px] text-center font-bold text-5xl text-white tracking-normal sm:leading-none sm:tracking-tight lg:pe-4 lg:text-left min-[470px]:max-w-2xl min-[470px]:text-7xl">
                  Pro betting meets pick selling
                </h1>
                <section className="mx-auto my-auto mt-10 max-w-prose text-center text-base text-white/70 leading-6 sm:max-w-2xl sm:text-base lg:text-left ">
                  <div className="mx-auto max-w-xl lg:mx-0">
                    RAS has gone 153-123-5 on NFL sides &amp; totals over the last 3 seasons &mdash;
                    55.43% &mdash; while beating the closing line 85% of the time. Every play is
                    released with a consensus line and independently verified at{' '}
                    <a
                      href="https://betstamp.app/u/RAS"
                      target="_blank"
                      className="text-white/70 underline"
                      rel="noreferrer"
                    >
                      betstamp
                    </a>
                    . Preseason through Super Bowl LXI.
                  </div>
                </section>
                <div className="mt-10 flex flex-col items-center justify-center gap-y-4 sm:flex-row sm:flex-wrap sm:gap-x-6 sm:gap-y-4 lg:justify-start">
                  {availableSports && (
                    <a
                      className="inline-flex w-full justify-center space-x-0.5 overflow-hidden whitespace-nowrap rounded-sm bg-emerald-400/10 px-6 py-3 font-medium text-base text-emerald-400 ring-1 ring-emerald-400 ring-inset delay-150 ease-in-out hover:bg-emerald-400/10 hover:text-emerald-300 hover:ring-emerald-300 sm:w-auto lg:text-sm"
                      href={!nflAvailable && cfbAvailable ? '/order/cfb' : '/order'}
                    >
                      {availableSports === 'NFL' || availableSports === 'CFB'
                        ? `Join ${availableSports} for 2026`
                        : 'Join for 2026'}
                    </a>
                  )}
                  <a
                    className="inline-flex w-full items-center justify-center space-x-1 overflow-hidden whitespace-nowrap rounded-sm px-6 py-3 font-medium text-base text-zinc-200 ring-1 ring-white/10 ring-inset transition hover:bg-white/5 hover:text-white sm:w-auto lg:text-sm"
                    href="/register"
                  >
                    <span>Sign up and get free picks</span>
                  </a>
                  <a
                    className="inline-flex w-full items-center justify-center space-x-1 overflow-hidden whitespace-nowrap py-3 font-normal text-sm text-zinc-200 transition hover:text-white sm:w-auto"
                    href="/about"
                  >
                    <span>Learn about RAS</span>
                    <ArrowRightIcon height={16} className="h-4 w-4" />
                  </a>
                </div>
              </div>
              <div className="mx-auto flex w-full flex-col rounded-sm bg-linear-to-b from-zinc-950/30 to-zinc-950/50 p-6 ring-1 ring-white/20 sm:w-full lg:flex lg:w-96 lg:flex-auto lg:justify-center">
                <div className="font-semibold text-base text-emerald-500">Football results</div>
                <span className="my-3 text-xs text-zinc-200">
                  Results third party verified by{' '}
                  <a
                    href="https://betstamp.app/u/RAS"
                    target="_blank"
                    className="text-white/70 underline"
                    rel="noreferrer"
                  >
                    betstamp
                  </a>
                  .
                </span>
                <div className="pt-1 pb-1 font-semibold text-sm text-white">
                  NFL sides &amp; totals
                </div>
                <Table>
                  <TableHeader className="hidden md:contents">
                    <TableRow>
                      <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm text-white sm:pr-3 sm:pl-0 dark:bg-transparent">
                        Season
                      </TableHead>
                      <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm text-white sm:pr-3 sm:pl-0 dark:bg-transparent">
                        Record
                      </TableHead>
                      <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm text-white sm:pr-3 sm:pl-0 dark:bg-transparent">
                        Units
                      </TableHead>
                      <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm text-white sm:pr-3 sm:pl-0 dark:bg-transparent">
                        Win rate
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nflResults.map((row) => (
                      <TableRow
                        key={row.season}
                        className="font-normal hover:group-[&]/tbody:bg-initial"
                      >
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 font-semibold text-sm text-zinc-200 sm:pr-3">
                          {row.season}
                        </TableCell>
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-200 sm:pr-3">
                          {row.win}-{row.loss}-{row.push}
                        </TableCell>
                        <TableCell
                          className={clsx(
                            'w-1/4 whitespace-nowrap py-2 pl-0 text-sm sm:pr-3',
                            (row.units ?? 0) >= 0 ? 'text-emerald-500' : 'text-red-400',
                          )}
                        >
                          {(row.units ?? 0) >= 0 ? '+' : ''}
                          {toFixed(row.units)}
                        </TableCell>
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-200 sm:pr-3">
                          {toFixed(((row.win ?? 0) / ((row.win ?? 0) + (row.loss ?? 0))) * 100)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="mt-4 pt-1 pb-1 font-semibold text-sm text-white">
                  CFB sides &amp; totals
                </div>
                <Table>
                  <TableHeader className="hidden md:contents">
                    <TableRow>
                      <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm text-white sm:pr-3 sm:pl-0 dark:bg-transparent">
                        Season
                      </TableHead>
                      <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm text-white sm:pr-3 sm:pl-0 dark:bg-transparent">
                        Record
                      </TableHead>
                      <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm text-white sm:pr-3 sm:pl-0 dark:bg-transparent">
                        Units
                      </TableHead>
                      <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-sm text-white sm:pr-3 sm:pl-0 dark:bg-transparent">
                        Win rate
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cfbResults.map((row) => (
                      <TableRow
                        key={row.season}
                        className="font-normal hover:group-[&]/tbody:bg-initial"
                      >
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 font-semibold text-sm text-zinc-200 sm:pr-3">
                          {row.season}
                        </TableCell>
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-200 sm:pr-3">
                          {row.win}-{row.loss}-{row.push}
                        </TableCell>
                        <TableCell
                          className={clsx(
                            'w-1/4 whitespace-nowrap py-2 pl-0 text-sm sm:pr-3',
                            (row.units ?? 0) >= 0 ? 'text-emerald-500' : 'text-red-400',
                          )}
                        >
                          {(row.units ?? 0) >= 0 ? '+' : ''}
                          {toFixed(row.units)}
                        </TableCell>
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-200 sm:pr-3">
                          {toFixed(((row.win ?? 0) / ((row.win ?? 0) + (row.loss ?? 0))) * 100)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            {/* <CurrentRecords /> */}
          </div>
        </div>
      </div>
    </div>
  );
}
